import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { GridSearch } from '../../components/GridSearch';
import { filterTestMethods, singleSelection, type TestMethodOption } from './itemTestsModel';
import './ItemTestsGrid.css';

const COLUMNS: AppColumn<TestMethodOption>[] = [
  { field: 'matrix', header: 'Matrix', width: 190 },
  { field: 'test', header: 'Test', width: 240 },
  { field: 'methodNumber', header: 'Method' },
];

/**
 * The "Test Method" popup behind the Matrix cell of the Assign Tests To Item →
 * Tests tab — legacy XAF lookup window (TestMethod_LookupListView): Search box at
 * the top right, Matrix · Test · Method grid sorted by Matrix then Test, pager with
 * page size, OK / Cancel. Clicking a row picks it at once; ticking one and pressing
 * OK does the same. Built from the shared AppDialog + AppDataGrid + GridSearch.
 */
export function TestMethodPickerDialog({ open, options, loading, currentId, onPick, onClose }: {
  open: boolean;
  /** Every live TestMethod, already in Matrix · Test · Method order. */
  options: TestMethodOption[];
  loading: boolean;
  /** The row's current test method, ticked when the popup opens. */
  currentId: string;
  onPick: (option: TestMethodOption) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  // Every opening starts clean, with the row's current choice ticked.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelected(currentId ? new Set([currentId]) : new Set());
  }, [open, currentId]);

  const rows = useMemo(() => filterTestMethods(options, search), [options, search]);
  const ticked = options.find((o) => selected.has(o.id));

  return (
    <AppDialog
      open={open}
      title="Test Method"
      width={800}
      className="dlg-center"
      maximizable
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={!ticked}
            onClick={() => { if (ticked) onPick(ticked); }}>
            OK
          </button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="itl-picker-search">
        <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
      </div>
      <AppDataGrid<TestMethodOption>
        rows={rows}
        columns={COLUMNS}
        getRowId={(o) => o.id}
        loading={loading}
        defaultPageSize={20}
        initialSort={{ key: 'matrix', dir: 'asc' }}
        selectable
        selectedIds={selected}
        onSelectionChange={(next) => {
          const kept = singleSelection(selected, next);
          if (kept) setSelected(kept);
        }}
        onRowClick={onPick}
        emptyText="No test methods"
      />
    </AppDialog>
  );
}
